/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect } from "react";
import FormFader from "../Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import _slice from "lodash/slice";
import ValidatorSelectField from "../../FormFields/ValidatorSelectField";
import { requiredValidator } from "../../../services/validationRules";
import { Box, MenuItem } from "@mui/material";
import _cloneDeep from "lodash/cloneDeep";
import { useForm } from "../../../provider/Form/FormProvider";
import _toNumber from "lodash/toNumber";
import Group from "../../Wizard/Group";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import CheckIcon from "../../../assets/icons/common/toggleable/IconCheck";
import PaymentObligation from "./PaymentObligation/PaymentObligation";
import Typography from "@mui/material/Typography";
import FormSubmitProps from "../../../types/FormSubmitProps";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import wizardStyle from "../wizardStyle";

type OtherPaymentObligationsProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function OtherPaymentObligations({ submitProps, product }: OtherPaymentObligationsProps) {
  const { values, handleChange, handleSubmit } = useForm();

  useEffect(() => {
    const numberOfPaymentObligations = values.numberOfPaymentObligations ? _toNumber(values.numberOfPaymentObligations) : 0;
    if (values.paymentObligations.length === numberOfPaymentObligations) {
      return;
    }
    let paymentObligations = _cloneDeep(values.paymentObligations);
    for (let i = paymentObligations.length; i < Math.min(numberOfPaymentObligations, 5); i++) {
      paymentObligations.push({
        name: "",
        remainingTerm: "",
        outstandingDebt: "",
        repayment: "",
        petitionerShare: "",
      });
    }
    paymentObligations = _slice(paymentObligations, 0, numberOfPaymentObligations);
    handleChange({
      target: {
        type: "other",
        name: "paymentObligations",
        value: paymentObligations,
      },
    });
  }, [values.paymentObligations, values.numberOfPaymentObligations]);

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={wizardStyle.wizardForm} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ausgaben
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "center",
            }}
          >
            <Grid size={12}>
              <Group
                type={"radio"}
                name={"hasOtherPaymentObligations"}
                question={"Haben Sie sonstige Zahlungsverpflichtungen oder Kredite, die Sie abbezahlen?"}
                options={[
                  {
                    labelText: "Nein",
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                  {
                    labelText: "Ja",
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.hasOtherPaymentObligations === "yes" && (
              <>
                <ValidatorSelectField
                  label="Wie viele sonstige Zahlungsverpflichtungen haben Sie?"
                  name="numberOfPaymentObligations"
                  validators={[requiredValidator("Bitte geben Sie an wie viele sonstige Zahlungsverpflichtungen Sie haben")]}
                >
                  <MenuItem value={"1"}>1</MenuItem>
                  <MenuItem value={"2"}>2</MenuItem>
                  <MenuItem value={"3"}>3</MenuItem>
                  <MenuItem value={"4"}>4</MenuItem>
                  <MenuItem value={"5"}>5</MenuItem>
                </ValidatorSelectField>
                {values.paymentObligations?.map((paymentObligation: any, index: number) => (
                  <PaymentObligation key={index} index={index} path={`paymentObligations[${index}]`} product={product} />
                ))}
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
